import React from "react";
import PropTypes from "prop-types";
import { BRAND_COLOURS, OTHER_COLOURS, resolveColour } from "./colours";
import styles from "./index.module.css";

/**
 * Colour name / HTML code field with the app's own suggestion list (not the
 * browser's <datalist>, which is unstyled and opens off to the side). Suggests
 * our standard colours, filtered as you type; any other CSS colour name or code
 * typed in still works.
 */
export default function SwatchInput({ id, label, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const listId = `${id}-list`;

  const query = String(value || "").trim().toLowerCase();
  const brand = Object.entries(BRAND_COLOURS);
  const other = Object.entries(OTHER_COLOURS);
  const all = [...brand, ...other];
  const exact = all.some(([name]) => name.toLowerCase() === query);
  const keep = (list) =>
    !query || exact || /^#/.test(query)
      ? list
      : list.filter(([name]) => name.toLowerCase().includes(query));
  const brandShown = keep(brand);
  const otherShown = keep(other);
  const options = [...brandShown, ...otherShown];

  const choose = (name) => {
    onChange(name);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(options.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter" && open && active >= 0 && options[active]) {
      e.preventDefault();
      choose(options[active][0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={styles.combo}>
      <input
        id={id}
        className={styles.swatchInput}
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder="Colour name or HTML code, e.g. Navy"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        tabIndex={-1}
        className={styles.comboToggle}
        aria-label={`Show colours for ${label}`}
        onMouseDown={(e) => {
          e.preventDefault();
          setOpen((o) => !o);
        }}
      >
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`} />
      </button>
      {open && options.length > 0 && (
        <ul id={listId} role="listbox" className={styles.comboList}>
          {options.map(([name, hex], i) => (
            <React.Fragment key={name}>
            {i === 0 && brandShown.length > 0 && (
              <li role="presentation" className={styles.comboGroup}>
                WWG brand colours
              </li>
            )}
            {i === brandShown.length && otherShown.length > 0 && (
              <li role="presentation" className={styles.comboGroup}>
                More colours
              </li>
            )}
            <li
              role="option"
              aria-selected={i === active}
              className={`${styles.comboOption} ${
                i === active ? styles.comboOptionActive : ""
              }`}
              // mousedown, not click: fires before the input's blur closes us.
              onMouseDown={(e) => {
                e.preventDefault();
                choose(name);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span className={styles.dot} style={{ background: hex }} />
              {name}
            </li>
            </React.Fragment>
          ))}
        </ul>
      )}
      {query && !resolveColour(value) && (
        <p className={styles.error}>
          Not a colour we recognise. Try a name like Navy or Heather Grey, or an
          HTML code like #1f2a44.
        </p>
      )}
    </div>
  );
}

SwatchInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

SwatchInput.defaultProps = {
  value: "",
};
